import React, { useRef, useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, FreeMode } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ProductCard from "../../../components/ProductCard/ProductCard";
import { useLanguage } from "../../../contexts/LanguageContext";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/free-mode";
import "./ProductSlider.css";

export default function ProductSlider({
  products,
  slidesPerView = {
    mobile:  1.5,
    tablet:  2.5,
    desktop: 3.5,
    large:   4.5,
  },
  spaceBetween  = 20,
  showNavigation= true,
  showPagination= false,
  loop          = false,
  freeMode      = true,
}) {
  const { lang } = useLanguage();
  const isAr     = lang === "ar";
  const swiperRef = useRef(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd]             = useState(false);
  const [swiperKey, setSwiperKey]     = useState(0);

  /* Re-mount swiper when language changes (RTL toggle) */
  useEffect(() => {
    setSwiperKey((prev) => prev + 1);
  }, [lang]);

  const handleSlideChange = (swiper) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  if (!products || products.length === 0) {
    return (
      <div className="product-slider-empty" dir={isAr ? "rtl" : "ltr"}>
        <p>{isAr ? "لا توجد منتجات" : "No products available"}</p>
      </div>
    );
  }

  return (
    <div className="product-slider-container">
      <div className="product-slider-box">
        {showNavigation && products.length > 3 && (
          <>
            <button
              className={`slider-nav-btn prev-btn ${isBeginning ? "at-start" : ""}`}
              onClick={() => swiperRef.current?.swiper.slidePrev()}
              aria-label={isAr ? "السابق" : "Previous"}
            >
              {isAr ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>

            <button
              className={`slider-nav-btn next-btn ${isEnd ? "at-end" : ""}`}
              onClick={() => swiperRef.current?.swiper.slideNext()}
              aria-label={isAr ? "التالي" : "Next"}
            >
              {isAr ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
            </button>
          </>
        )}

        <div className="swiper-wrapper-box">
          <Swiper
            key={swiperKey}
            ref={swiperRef}
            modules={[Navigation, Pagination, FreeMode]}
            spaceBetween={spaceBetween}
            slidesPerView={slidesPerView.mobile}
            freeMode={freeMode}
            loop={loop}
            pagination={showPagination ? { clickable: true, dynamicBullets: true } : false}
            onSlideChange={handleSlideChange}
            breakpoints={{
              480: {
                slidesPerView: slidesPerView.tablet  || 2.5,
                spaceBetween: 16,
              },
              768: {
                slidesPerView: slidesPerView.desktop || 3.5,
                spaceBetween: 20,
              },
              1024: {
                slidesPerView: slidesPerView.large   || 4.5,
                spaceBetween: 20,
              },
              1280: {
                slidesPerView: 5,
                spaceBetween: 24,
              },
            }}
            className="product-swiper-organized"
            dir={isAr ? "rtl" : "ltr"}
          >
            {products.map((product) => (
              <SwiperSlide key={product.id} className="product-slide-organized">
                <div className="product-card-wrapper">
                  <ProductCard product={product} />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      {showPagination && <div className="slider-custom-pagination" />}
    </div>
  );
}